import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Video, Loader2, Calendar, KeyRound, Radio, Clock } from "lucide-react";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import api from "@/lib/api";
import { CalendarButton } from "@/components/meeting/CalendarButton";
import { LobbyPreview } from "@/components/meeting/LobbyPreview";
import { BookCallCard } from "@/components/booking/BookCallCard";

const when = (c) => {
  if (c.start_at) return new Date(c.start_at).toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  return [c.scheduled_at, c.scheduled_time].filter(Boolean).join(" ") || "Time to be confirmed";
};

const JoinByCode = ({ onJoined }) => {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const join = async (e) => {
    e.preventDefault(); setBusy(true);
    try { const { data } = await api.post("/api/conferences/join-code", { code }); setCode(""); onJoined(data); }
    catch (err) { toast.error(err.response?.data?.detail || "Couldn't find that meeting"); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={join} className="bg-slate-900 text-white rounded-xl p-5 mb-4" data-testid="join-by-code-form">
      <p className="font-semibold flex items-center gap-2 mb-1"><KeyRound className="w-4 h-4 text-yellow-400" />Have a meeting code?</p>
      <p className="text-sm text-slate-300 mb-3">Enter the Room Code or Guest Code from your invite email or text.</p>
      <div className="flex gap-2 max-w-md">
        <Input value={code} onChange={e => setCode(e.target.value.toUpperCase())} placeholder="e.g. 3F9A2C1B" className="bg-white text-slate-900 font-mono tracking-widest" data-testid="join-code-input" />
        <button disabled={busy || code.trim().length < 4} className="bg-yellow-400 hover:bg-yellow-300 disabled:opacity-50 text-slate-900 font-bold px-5 rounded-lg flex items-center gap-1" data-testid="join-code-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Video className="w-4 h-4" />}Join</button>
      </div>
    </form>
  );
};

const Row = ({ c, waiting, onJoin }) => {
  const live = c.status === "active";
  return (
    <div className={`border rounded-xl p-4 flex flex-wrap items-center gap-3 ${live ? "border-green-400 bg-green-50/50" : "border-slate-200"}`} data-testid={`meeting-${c.id}`}>
      <div className="flex-1 min-w-[180px]">
        <h4 className="font-semibold text-slate-900 flex items-center gap-2">{c.title}{live && <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase text-green-700 bg-green-100 px-2 py-0.5 rounded-full"><Radio className="w-3 h-3 animate-pulse" />Live now</span>}</h4>
        <p className="text-xs text-slate-500 flex items-center gap-1 mt-0.5"><Calendar className="w-3 h-3" />{when(c)} · Host: {c.host_name}</p>
      </div>
      {c.status === "ended" ? <span className="text-xs bg-slate-100 text-slate-500 px-3 py-1 rounded-full">Ended</span> : (<>
        {!live && <CalendarButton c={c} />}
        <button onClick={() => onJoin(c)} className={`px-4 py-2 rounded-lg text-sm font-bold flex items-center gap-1 text-white ${live ? "bg-green-600 hover:bg-green-500" : waiting ? "bg-slate-500" : "bg-blue-600 hover:bg-blue-500"}`} data-testid={`join-meeting-${c.id}`}>
          {waiting ? <><Loader2 className="w-4 h-4 animate-spin" />Waiting for host…</> : <><Video className="w-4 h-4" />{live ? "Join now" : "Join"}</>}
        </button>
      </>)}
      {waiting && <div className="w-full"><LobbyPreview title="While you wait — check your camera & mic" /></div>}
    </div>
  );
};

export const CustomerMeetings = () => {
  const nav = useNavigate();
  const [confs, setConfs] = useState(null);
  const [waiting, setWaiting] = useState(null);
  const [testing, setTesting] = useState(false);
  const waitRef = useRef(null);
  waitRef.current = waiting;
  const load = useCallback(() => api.get("/api/conferences").then(r => {
    setConfs(r.data);
    const w = waitRef.current && r.data.find(c => c.id === waitRef.current);
    if (w?.status === "active") { toast.success("The host has started — joining now"); nav(`/conference/${w.id}`); }
  }).catch(() => setConfs([])), [nav]);
  useEffect(() => { load(); const t = setInterval(load, waiting ? 5000 : 20000); return () => clearInterval(t); }, [load, waiting]);
  const onJoin = (c) => {
    if (c.status === "active") return nav(`/conference/${c.id}`);
    if (waiting === c.id) { setWaiting(null); return undefined; }
    setWaiting(c.id); toast.info("The host hasn't started yet — keep this page open and we'll bring you in automatically.");
    return undefined;
  };
  const onJoined = (c) => { load(); onJoin(c); };
  if (!confs) return <Loader2 className="w-6 h-6 animate-spin text-blue-600 mx-auto my-10" />;
  const open = confs.filter(c => c.status !== "ended").sort((a, b) => (b.status === "active") - (a.status === "active"));
  const past = confs.filter(c => c.status === "ended").slice(0, 5);
  return (
    <div data-testid="customer-meetings">
      <BookCallCard onBooked={load} />
      <JoinByCode onJoined={onJoined} />
      <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
        <div className="flex flex-wrap items-center gap-2 mb-1">
          <h2 className="text-lg font-semibold text-slate-900 flex-1">Your meetings</h2>
          {!waiting && <button onClick={() => setTesting(!testing)} className="text-sm font-semibold text-blue-600 hover:underline flex items-center gap-1" data-testid="test-devices-btn"><Video className="w-4 h-4" />{testing ? "Close test" : "Test camera & mic"}</button>}
        </div>
        {testing && !waiting && <div className="mb-4"><LobbyPreview onClose={() => setTesting(false)} /></div>}
        <p className="text-slate-500 text-sm mb-4 flex items-center gap-1"><Clock className="w-3.5 h-3.5" />Click Join any time — if the host hasn't started yet, we'll let you in the moment they do.</p>
        {open.length === 0 && <p className="text-slate-400 text-sm text-center py-8" data-testid="meetings-empty">No upcoming meetings. Use a code above or wait for an invite from your ShopAdvisor team.</p>}
        <div className="space-y-3">{open.map(c => <Row key={c.id} c={c} waiting={waiting === c.id} onJoin={onJoin} />)}</div>
        {past.length > 0 && <><p className="text-xs font-bold uppercase tracking-wider text-slate-400 mt-6 mb-2">Past meetings</p><div className="space-y-2">{past.map(c => <Row key={c.id} c={c} onJoin={onJoin} />)}</div></>}
      </div>
    </div>
  );
};
